/**
 * PATH: packages/ui/src/components/BoxResizeHandles.tsx
 * FILE_ID: UI-CMP-BOX-RESIZE-HANDLES-001
 * PURPOSE: Eight absolutely-positioned resize grips. Parent must be relative
 *          (or fixed). Pointer capture keeps the drag alive past the 4px bar.
 * WHY: One component so the ITP popup and any later floating panel share
 *      the FilesDrawer-style 8-way affordance without copying its mouse
 *      window-listener path.
 */
import { useRef } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import {
  applyBoxResize,
  BOX_RESIZE_AXES,
  BOX_RESIZE_CURSORS,
  type Box,
  type BoxBounds,
  type BoxResizeAxis,
} from './boxResize'

const AXIS_NAMES: Record<BoxResizeAxis, string> = {
  n: 'top',
  s: 'bottom',
  e: 'right',
  w: 'left',
  ne: 'top-right',
  nw: 'top-left',
  se: 'bottom-right',
  sw: 'bottom-left',
}

const AXIS_POS: Record<BoxResizeAxis, string> = {
  n: 'top-0 left-3 right-3 h-2 cursor-ns-resize',
  s: 'bottom-0 left-3 right-3 h-2 cursor-ns-resize',
  w: 'left-0 top-3 bottom-3 w-2 cursor-ew-resize',
  e: 'right-0 top-3 bottom-3 w-2 cursor-ew-resize',
  ne: 'top-0 right-0 z-[2] h-4 w-4 cursor-nesw-resize',
  nw: 'top-0 left-0 z-[2] h-4 w-4 cursor-nwse-resize',
  se: 'bottom-0 right-0 z-[2] h-4 w-4 cursor-nwse-resize',
  sw: 'bottom-0 left-0 z-[2] h-4 w-4 cursor-sw-resize',
}

export function BoxResizeHandles({
  box,
  bounds,
  onChange,
  axisLabels,
  label,
}: {
  box: Box
  bounds: BoxBounds
  onChange: (next: Box) => void
  axisLabels?: Partial<Record<BoxResizeAxis, string>>
  label: string
}): ReactNode {
  const drag = useRef<{
    axis: BoxResizeAxis
    startX: number
    startY: number
    box: Box
  } | null>(null)

  const onPointerDown = (axis: BoxResizeAxis) => (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    drag.current = { axis, startX: event.clientX, startY: event.clientY, box }
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      /* jsdom / unsupported */
    }
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (!current) return
    onChange(applyBoxResize(
      current.box,
      current.axis,
      event.clientX - current.startX,
      event.clientY - current.startY,
      bounds,
    ))
  }

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    drag.current = null
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      {BOX_RESIZE_AXES.map((axis) => (
        <div
          key={axis}
          role="separator"
          aria-label={axisLabels?.[axis] ?? `Resize ${label} ${AXIS_NAMES[axis]}`}
          aria-orientation={axis === 'n' || axis === 's' ? 'horizontal' : 'vertical'}
          tabIndex={-1}
          onPointerDown={onPointerDown(axis)}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className={`absolute touch-none hover:bg-primary/30 ${AXIS_POS[axis]}`}
          style={{ cursor: BOX_RESIZE_CURSORS[axis] }}
        >
          {axis === 'se' ? (
            <span
              aria-hidden
              className="pointer-events-none absolute bottom-0.5 right-0.5 h-2.5 w-2.5 border-b-2 border-r-2 border-border"
            />
          ) : null}
        </div>
      ))}
    </>
  )
}
